import {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Button from 'components/Button';
import Notification from 'components/Notification';
import Panel from 'components/Panel';
import SignOffDocumentModal from 'pages/Portal/ClientInboxItem/SignOffDocumentModal';
import {
    AcceptOrderState,
    ClientInboxItemBackend,
} from 'pages/Portal/ClientInboxItem/types';
import {useBackend} from 'state/backend';

const AcceptOrderPanel: FC = () => {
    const {
        data: {
            canAcceptOrder,
            document,
            loggedInPartner,
            orderAccepted,
            portalMid,
            sendingPartner,
        },
    } = useBackend<ClientInboxItemBackend>();
    const {t} = useTranslation('Portal');

    const type =
        document.documentType === 'acceptance' ? 'confirmation' : 'order';

    const [isOpen, setIsOpen] = useState(false);
    const [state, setState] = useState<AcceptOrderState>(() => {
        if (loggedInPartner?.mid === sendingPartner.mid) {
            return 'ownDocument';
        }

        if (document.dateCancelled) {
            return 'cancelled';
        }

        if (orderAccepted) {
            return 'accepted';
        }

        if (canAcceptOrder) {
            return 'signable';
        }

        return undefined;
    });

    if (!canAcceptOrder) {
        return null;
    }

    let content;

    if (state === 'ownDocument') {
        content = (
            <Notification type="alert">
                {t(`signOffDocumentModal.${type}.ownDocument`)}
            </Notification>
        );
    } else if (state === 'cancelled') {
        content = (
            <Notification type="alert">
                {t('signOffDocumentModal.cancelled')}
            </Notification>
        );
    } else if (state === 'accepted') {
        content = (
            <Notification type="success">
                {t(`signOffDocumentModal.${type}.accepted`)}
            </Notification>
        );
    } else if (state === 'signable') {
        content = (
            <Button
                className="w-full"
                kind="secondary"
                onClick={() => {
                    setIsOpen(true);
                }}
                size="base"
            >
                {t(`signOffDocumentModal.${type}.signOff`)}
            </Button>
        );
    }

    return (
        <>
            <Panel title={t(`signOffDocumentModal.${type}.title`)}>
                <div className="flex justify-between mb-4 font-medium">
                    <p>{t('signOffDocumentModal.total')}</p>
                    <p>{document.total}</p>
                </div>
                {content}
            </Panel>
            <SignOffDocumentModal
                document={document}
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
                portalMid={portalMid}
                setState={setState}
            />
        </>
    );
};

export default AcceptOrderPanel;
